/**
 * Marker for Bugsnap's own chrome (the trigger button and the report dialog).
 *
 * Transient page UI — action menus, dropdowns, popovers — closes itself on a
 * document-level `mousedown`/`click` that lands outside its own subtree. That is
 * exactly what a user reporting a bug about such UI does when they reach for
 * Bugsnap, so the state they wanted to snapshot is gone before the dialog opens.
 *
 * The Bugsnap trigger and the dialog's Screenshot button swallow their pointer
 * events outright (see ./useShieldedTrigger), which protects every such component
 * app-wide at the two moments that matter. This marker is the opt-in for the rest
 * of the dialog's lifetime: a component that checks `isBugsnapUiEvent` in its
 * outside-click/scroll handler also stays open while the user fills in the report.
 */

/** Spread onto the Bugsnap trigger/dialog root: `<div {...BUGSNAP_UI_PROPS}>`. */
export const BUGSNAP_UI_PROPS = { 'data-bugsnap-ui': '' } as const

const BUGSNAP_UI_SELECTOR = '[data-bugsnap-ui]'

/**
 * True when `node` is (or sits inside) Bugsnap's own chrome.
 *
 * Accepts an `EventTarget` so handlers can pass `event.target` directly;
 * non-element targets (`document`, `window`, text nodes' owners) are handled.
 */
function isBugsnapUiNode(node: EventTarget | null | undefined): boolean {
  if (!(node instanceof Node)) return false
  const element = node instanceof Element ? node : node.parentElement
  return !!element?.closest(BUGSNAP_UI_SELECTOR)
}

/**
 * True when an outside-click (or scroll) handler should ignore this event because it
 * is the user operating Bugsnap rather than the page.
 *
 * @param target - `event.target`.
 * @param self - Any node belonging to the component that is asking. Components that
 *   live inside Bugsnap's chrome themselves — the dialog's own dropdowns — get normal
 *   behaviour: for them the dialog is not "outside", it is their own page.
 */
export function isBugsnapUiEvent(
  target: EventTarget | null | undefined,
  self?: Node | null,
): boolean {
  return isBugsnapUiNode(target) && !isBugsnapUiNode(self)
}
